<template>
  <div
    v-show="links"
    class="secondary-header"
    :class="{
      [$class('hidden')]: mobileOnly && !isMobile,
      'secondary-header--delayed': !isVisible
    }"
  >
    <div class="secondary-header__wrapper container">
      <div class="secondary-header__nav-wrapper">
        <nav
          ref="nav"
          class="secondary-header__nav"
        >
          <button
            v-for="(link, index) in links"
            :key="index"
            class="secondary-header__link"
            :class="
              { [$class('active')]: link.sectionReference === activeSection }
            "
            :data-section-reference="link.sectionReference"
            @click.prevent="handleLinkClick(link.sectionReference)"
          >
            <img
              v-if="link.icon"
              class="secondary-header__link-icon"
              :src="link.icon"
            >
            <span
              class="text-body-2xs secondary-header__link-label"
              v-text="link.title"
            />
          </button>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Secondary header (secondary-header)
 * -----------------------------------------------------------------------------
 * Displays in-page links and product information on product pages.
 *
 *  @param {Number} headerHeight - Height of the site header.
 *  @param {Array} links - Navigation links. Each item in the array should have
 *    the properties `title` and `sectionReference`
 *  @param {Object} product - Data for the current product.
 *  @param {Object} variant - Data for the selected variant.
 */
import { mapState } from 'vuex'

import { setIntersectionObserver, throttle } from '~/helpers/general'

export default {
  name: 'SecondaryHeader',

  props: {
    headerHeight: {
      type: Number,
      default: 0,
    },
    links: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      activeSection: '',
      pageSections: [],
      state: 'ready',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      settings: (state) => state.index.settings,
      mobileOnly: (state) => state.header.secondaryHeaderMobileOnly,
      isMobile: (state) => state.index.screen.mobile,
      isVisible: (state) => state.header.secondaryHeaderVisible,
    }),

    /**
     * Compute text for the submit button.
     * @returns {String}
     */
    submitText() {
      let string = 'product.cta.add_to_cart'

      if (!this.variant.available) {
        string = 'product.cta.sold_out'
      }

      if (
        this.variant.preOrder &&
        this.variant.preOrder.active
      ) {
        string = 'product.cta.pre_order'
      }

      return this.$string(string)
    },
  },

  watch: {

    /**
     * When the active section changes, scroll the navigation bar to display the
     * link for that section, if one exists.
     */
    activeSection() {
      const link = this.$refs.nav.querySelector(
        `[data-section-reference="${this.activeSection}"]`,
      )

      if (!link || !this.getNavScrollable) {
        return
      }

      this.$refs.nav.scrollLeft = link.offsetLeft
    },
  },

  /**
   * Set page sections, navigation gradient, and event listeners.
   */
  mounted() {
    this.pageSections = this.setPageSections()
    this.setNavScrollListener()
    this.setSectionObservers()
  },

  methods: {

    /**
     * Check if the nav element is scrollable.
     * @returns {Boolean}
     */
    getNavScrollable() {
      const nav = this.$refs.nav

      if (!nav) {
        return false
      }

      return nav.scrollWidth > nav.offsetWidth
    },

    /**
     * When a link is clicked, scroll the page to the associated section.
     * - The header height is used as an offset, to ensure that the top of the
     *   section is visible.
     *
     * @param {String} sectionReference - id of the linked section.
     */
    handleLinkClick(sectionReference = '') {
      const section = document.getElementById(sectionReference)

      if (!section) {
        return
      }

      const offset = section.offsetTop - 2 * this.headerHeight
      window.scrollTo(0, offset)
    },

    /**
     * When the navigation element is scrolled, check if the gradient should be
     * shown or hidden.
     * - If the navigation element is scrolled more than 10px away from its
     *   right edge, the gradient is shown.
     */
    handleNavScroll() {
      const nav = this.$refs.nav
      const remainingDistance =
        nav.scrollWidth - nav.offsetWidth - nav.scrollLeft

      this.showNavGradient = remainingDistance > 10
    },

    /**
     * Check for the section's id before setting it as the active section.
     * @param {Array} entries - Array of IntersectionObserverEntry objects.
     */
    handleSectionIntersection(entries = []) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const id = entry.target.id

          if (!id) {
            return
          }

          this.activeSection = id
        }
      }
    },

    /**
     * Listen for scroll events on the navigation element.
     */
    setNavScrollListener() {
      this.$refs.nav.addEventListener(
        'scroll',
        throttle(() => this.handleNavScroll(), this.settings.throttle),
      )
    },

    /**
     * Create an intersection observer for each linked page section.
     * - Using negative vertical margins triggers the callback when the section
     *   is in the center of the viewport.
     */
    setSectionObservers() {
      for (const pageSection of this.pageSections) {
        setIntersectionObserver({
          callback: this.handleSectionIntersection,
          element: pageSection,
          rootMargin: '-50% 0px -50%',
          threshold: 0,
        })
      }
    },

    /**
     * For each link in the secondary header, look for a section on the page
     * with a matching id attribute.
     * @returns {Array}
     */
    setPageSections() {
      const linkReferences = this.links.map((link) => {
        return link.sectionReference
      })

      const sections = Array.from(
        document.querySelectorAll('section[data-component-handle]'),
      )

      return sections.filter((section) => {
        const id = section.id
        return id && linkReferences.includes(id)
      })
    },
  },
}
</script>

<style lang="scss">
@import './secondary-header';
</style>
